import { GhostTextarea } from './ghost'
import { acceptPending, addPending, failPending, myName, settleWith, takeRestored, usePending, type Pending } from './outbox'
import { Kbd } from './glyphs'
import { addresses, RecipientInput } from './recipients'
import { useCallback, useEffect, useRef, useState } from 'react'
import { failureText, inbox, type MailboxMessageSummary } from './api'
import { MessageBody } from './message-body'
import { TAG_FOR_REASON } from './themes'
import { senderRule, type Conversation, type MailState } from './store'
import { Avatar, senderCheck, stamp, Tile } from './visuals'

type Full = Awaited<ReturnType<typeof inbox.mailboxMessage>>
export type ReplyMode = 'reply' | 'replyAll' | 'forward'

const cache = new Map<string, Promise<Full>>()
/** Messages that have finished loading, so a thread can render its HTML on the very first frame. */
const ready = new Map<string, Full>()
export function loadMessage(mailboxId: string, id: string) {
  let pending = cache.get(id)
  if (!pending) {
    pending = inbox.mailboxMessage(mailboxId, id)
    cache.set(id, pending)
    pending.then(value => { ready.set(id, value) }, () => cache.delete(id))
    if (cache.size > 300) { const oldest = cache.keys().next().value!; cache.delete(oldest); ready.delete(oldest) }
  }
  return pending
}

const warmedImages = new Set<string>()
/**
 * Fetch a conversation's newest message (and start its first images) in the
 * background, so opening it shows the finished email straight away.
 */
export function prefetchConversation(item: Conversation) {
  const message = item.messages[item.messages.length - 1]
  const mailboxId = message?.memberships[0]?.mailboxId
  if (!message || !mailboxId || ready.has(message.id)) return
  void loadMessage(mailboxId, message.id).then(full => {
    const html = full.bodyDocument?.html ?? full.bodyHtml ?? ''
    let count = 0
    for (const match of html.matchAll(/<img\b[^>]*?\ssrc=["']([^"']+)["']/gi)) {
      const src = match[1]!.replace(/&amp;/g, '&')
      if (count++ >= 8) break
      if (warmedImages.has(src) || !/^(https?:|\/)/.test(src)) continue
      warmedImages.add(src)
      const image = new Image(); image.referrerPolicy = 'no-referrer'; image.src = src
    }
  }).catch(() => {})
}

/** Send a reply or forward through the SDK: prepare a draft from the message, then submit it. Returns the operation (for Undo). */
export async function sendReply(message: MailboxMessageSummary, mode: ReplyMode, bodyText: string, extra: { to?: { name: string; email: string }[] } = {}) {
  const mailboxId = message.memberships[0]?.mailboxId
  const draft = await inbox.createDraft({
    accountId: message.accountId, ...(mailboxId ? { mailboxId } : {}), mode, sourceMessageId: message.id,
    ...(mode === 'forward' ? {} : { bodyText }),
    ...(extra.to ? { to: extra.to } : {}),
  })
  let current = draft
  if (mode === 'forward') current = await inbox.updateDraft(draft.id, { bodyText: `${bodyText}\n\n---------- Forwarded message ----------\n${draft.bodyText}` }, draft.revision)
  return inbox.submit(current.id, { revision: current.revision, idempotencyKey: crypto.randomUUID() })
}

export function Thread({ item, mail, replyRequest, onSent, onKeyFromFrame }: {
  item: Conversation; mail: MailState
  /** Bumped by the shell when r / ⇧R / f is pressed. */
  replyRequest: { mode: ReplyMode; at: number } | null
  onSent: (operationId: string, label: string) => void
  onKeyFromFrame: (event: KeyboardEvent) => void
}) {
  const messages = item.messages
  const latest = messages[messages.length - 1]!
  const [open, setOpen] = useState<Set<string>>(() => new Set([latest.id]))
  const [full, setFull] = useState<Record<string, Full>>(() => Object.fromEntries(messages.flatMap(message => { const value = ready.get(message.id); return value ? [[message.id, value]] : [] })))
  const [loadError, setLoadError] = useState('')

  useEffect(() => { setOpen(new Set([latest.id])) }, [item.key, latest.id])
  useEffect(() => {
    let cancelled = false
    for (const message of messages) {
      if (!open.has(message.id) || full[message.id] || ready.has(message.id)) continue
      const mailboxId = message.memberships[0]?.mailboxId
      if (!mailboxId) continue
      loadMessage(mailboxId, message.id)
        .then(value => { if (!cancelled) setFull(previous => ({ ...previous, [message.id]: value })) })
        .catch(error => { if (!cancelled) setLoadError(failureText(error)) })
    }
    return () => { cancelled = true }
  }, [open, messages, full])

  // Replies you just sent show here right away (see outbox.ts); the synced Sent copy replaces them.
  const pending = usePending().filter(p => p.conversation === item.key)
  useEffect(() => {
    const sent = messages.filter(m => mail.ownAddresses.has(m.from.email.toLowerCase()))
    if (sent.length) settleWith(item.key, sent)
  }, [messages, item.key, mail.ownAddresses])

  const d = item.decision
  const from = item.lead.from
  const firstName = (from.name || from.email).split(/[\s@]/)[0]
  const earlier = messages.slice(0, -1)
  // A sender/domain rule you taught decides placement; don't call it urgent if you filed it under Other.
  const rule = senderRule(mail.rules, from.email)
  const matters = rule?.action !== 'other'

  return (
    <div className="thread">
      <div className="thread-col view-in" key={item.key}>
        <div className="why">
          {item.account && <Tile brand={item.account.brand} tone={item.account.tone} size={16} />}
          <strong>{firstName}</strong>
          <span>{matters && d?.needsReply ? 'is waiting on your reply' : matters && d?.important ? 'wrote to you' : 'sent this'}</span>
          {matters && d?.important && d.reason && <span className={`why-tag${d.critical ? ' critical' : ''}`} data-tag={TAG_FOR_REASON[d.reason]}>{d.reason}</span>}
          {(rule || d) && <span className="faint">· {rule?.action === 'other' ? rule.label : d?.label}</span>}
        </div>
        <h1 className="subject">{item.subject}</h1>
        {earlier.length > 0 && (
          <div className="earlier">
            {earlier.map(message => open.has(message.id)
              ? <OpenMessage key={message.id} own={mail.ownAddresses.has(message.from.email.toLowerCase())} message={message} full={full[message.id] ?? ready.get(message.id)} onCollapse={() => setOpen(prev => { const next = new Set(prev); next.delete(message.id); return next })} onKey={onKeyFromFrame} compact />
              : (
                <button key={message.id} className="earlier-row" onClick={() => setOpen(prev => new Set(prev).add(message.id))}>
                  <span className="earlier-from">{mail.ownAddresses.has(message.from.email.toLowerCase()) ? 'You' : message.from.name || message.from.email}</span>
                  <span className="earlier-line">{message.preview}</span>
                  <span className="faint">{stamp(message.receivedAt)}</span>
                </button>
              ))}
          </div>
        )}
        <OpenMessage message={latest} own={mail.ownAddresses.has(latest.from.email.toLowerCase())} full={full[latest.id] ?? ready.get(latest.id)} onKey={onKeyFromFrame} person={d ? d.sender === 'person' : true} />
        {pending.map(p => <PendingMessage key={p.localId} pending={p} />)}
        {loadError && <p className="warn small">{loadError}</p>}
        <InlineReply key={item.key} item={item} message={item.lead} request={replyRequest} onSent={onSent} />
      </div>
    </div>
  )
}

function OpenMessage({ message, full, onCollapse, onKey, compact, person = true, own = false }: { message: MailboxMessageSummary; full: Full | undefined; onCollapse?: () => void; onKey: (event: KeyboardEvent) => void; compact?: boolean; person?: boolean; own?: boolean }) {
  const recipients = full ? [...full.to, ...full.cc].map(p => p.name || p.email).join(', ') : ''
  const [details, setDetails] = useState(false)
  const check = senderCheck(message.from, person)
  const list = (people: { name: string; email: string }[]) => people.map(p => p.name ? `${p.name} <${p.email}>` : p.email).join(', ')
  return (
    <article className={`message${compact ? ' compact' : ''}`}>
      <header className="message-head" onClick={onCollapse} style={onCollapse ? { cursor: 'pointer' } : undefined}>
        <Avatar name={message.from.name} email={message.from.email} person={person} size={20} />
        <button className="sender-button" onClick={event => { event.stopPropagation(); setDetails(!details) }} title="Who sent this">
          <strong>{message.from.name || (own ? 'You' : message.from.email)}</strong>
          {!own && <span className="faint">{message.from.email}</span>}
        </button>
        {check.mismatch && <button className="sender-warning" onClick={event => { event.stopPropagation(); setDetails(true) }}>Not from {check.claimed.split(/\s+/)[0]}</button>}
        {recipients && !details && <span className={`ellipsis${own ? ' muted' : ' faint'}`}>to {recipients}</span>}
        <span className="faint push">{stamp(message.receivedAt)}</span>
      </header>
      {details && (
        <dl className="sender-details">
          {check.mismatch && <div className="sender-note">The name says <strong>{check.claimed}</strong>, but it was sent from <strong>{check.site}</strong>{check.via ? `, forwarded by ${check.via}` : ''}. Check links before clicking.</div>}
          {!check.mismatch && check.via && <div className="sender-note muted">Forwarded by {check.via} from {check.site}.</div>}
          <dt>From</dt><dd>{list([message.from])}</dd>
          {full?.replyTo?.length ? <><dt>Reply-to</dt><dd>{list(full.replyTo)}</dd></> : null}
          {full?.to.length ? <><dt>To</dt><dd>{list(full.to)}</dd></> : null}
          {full?.cc.length ? <><dt>Cc</dt><dd>{list(full.cc)}</dd></> : null}
          <dt>Date</dt><dd>{new Date(message.receivedAt).toLocaleString()}</dd>
          <dt>Sent from</dt><dd>{message.from.email.split('@')[1]}</dd>
        </dl>
      )}
      {full
        ? <MessageBody html={full.bodyDocument?.html ?? full.bodyHtml} styles={full.bodyDocument?.styles ?? ''} text={full.bodyText} {...(full.bodyFormat ? { format: full.bodyFormat } : {})} onKey={onKey} />
        : <div className="body-loading" aria-label="Loading" />}
      {full?.attachments.length ? (
        <div className="attachments">{full.attachments.filter(a => !a.inline).map(a => (
          <a key={a.id} href={`/api/inbox/v1/blobs/${encodeURIComponent(a.id)}`} target="_blank" rel="noopener" className="attachment">{a.filename} <span className="faint">{Math.max(1, Math.round(a.size / 1024))} KB</span></a>
        ))}</div>
      ) : null}
    </article>
  )
}

function InlineReply({ item, message, request, onSent }: { item: Conversation; message: MailboxMessageSummary; request: { mode: ReplyMode; at: number } | null; onSent: (operationId: string, label: string) => void }) {
  const [mode, setMode] = useState<ReplyMode>('reply')
  const [text, setText] = useState('')
  const [to, setTo] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const area = useRef<HTMLTextAreaElement>(null)
  const toField = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!request) return
    setMode(request.mode)
    requestAnimationFrame(() => (request.mode === 'forward' ? toField.current ?? area.current : area.current)?.focus())
  }, [request])

  const send = useCallback(async () => {
    if (!text.trim() || sending) return
    if (mode === 'forward' && !to.trim()) { setError('Add who to forward it to.'); return }
    setSending(true); setError('')
    const body = text.trim(), recipients = mode === 'forward' ? addresses(to) : undefined
    // Show it in the conversation now; the server catches up behind it.
    const toNames = recipients ? recipients.map(r => r.name || r.email).join(', ') : mode === 'replyAll' ? `${message.from.name || message.from.email} and others` : message.from.name || message.from.email
    const localId = addPending({ conversation: item.key, text: body, to: toNames })
    setText(''); setTo('')
    try {
      const operation = await sendReply(message, mode, body, recipients ? { to: recipients } : {})
      const policy = await inbox.policy().catch(() => ({ undoSendSeconds: 10 }))
      acceptPending(localId, operation.id, policy.undoSendSeconds, operation.sendAt)
      onSent(operation.id, mode === 'forward' ? 'Forwarded' : 'Reply sent')
    } catch (failure) {
      failPending(localId)
      setText(body)
      setError(failureText(failure))
    } finally {
      setSending(false)
    }
  }, [text, sending, mode, to, message, onSent, item.key])
  // Undo puts the text back here.
  const all = usePending()
  useEffect(() => { const back = takeRestored(item.key); if (back) { setText(back); requestAnimationFrame(() => area.current?.focus()) } }, [all, item.key])

  const name = (message.from.name || message.from.email).split(/[\s@]/)[0]
  const verb = mode === 'forward' ? 'Forward' : mode === 'replyAll' ? 'Reply all' : `Reply to ${name}`
  const placeholder = mode === 'forward' ? 'Add a note…' : 'Draft reply…'
  return (
    <div className="reply">
      <div className="reply-head">
        <span className="muted small">{verb}</span>
        <span className="faint small key-line">{text ? <><Kbd k="⌘↵" /> send <Kbd k="esc" /> leave</> : <><Kbd k="r" /> reply <Kbd k="⇧R" /> all <Kbd k="f" /> forward</>}</span>
      </div>
      {mode === 'forward' && <RecipientInput ref={toField} className="reply-to" placeholder="To" value={to} onChange={setTo} />}
      <GhostTextarea ref={area} className="reply-box" rows={text ? Math.min(14, text.split('\n').length + 2) : 2}
        placeholder={placeholder} value={text} onChange={setText}
        context={{ messageId: message.id, ...(message.memberships[0]?.mailboxId ? { mailboxId: message.memberships[0].mailboxId } : {}), ...(mode === 'forward' ? { to } : {}) }}
        onKeyDown={event => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) { event.preventDefault(); void send() }
          if (event.key === 'Escape') { event.preventDefault(); (event.target as HTMLTextAreaElement).blur() }
          event.stopPropagation()
        }} />
      {error && <div className="small warn">{error}</div>}
    </div>
  )
}

/** A reply you just sent, shown before the server has it. Counts down the undo window, then says Sent. */
function PendingMessage({ pending }: { pending: Pending }) {
  const [name, setName] = useState('You')
  const [now, setNow] = useState(Date.now())
  useEffect(() => { void myName().then(setName) }, [])
  useEffect(() => { if (pending.status !== 'scheduled') return; const id = setInterval(() => setNow(Date.now()), 250); return () => clearInterval(id) }, [pending.status])
  const left = pending.sendAt ? Math.max(0, Math.ceil((pending.sendAt - now) / 1000)) : null
  const status = pending.status === 'failed' ? 'Didn’t send' : pending.status === 'sent' || left === 0 ? 'Sent' : pending.status === 'queued' ? 'Sending…' : `Sending in ${left} s`
  return (
    <article className={`message pending-message ${pending.status}`}>
      <header className="message-head">
        <Avatar name={name} email="" person size={20} />
        <strong>{name}</strong>
        <span className="muted ellipsis">to {pending.to}</span>
        <span className={`push small pending-status${pending.status === 'failed' ? ' warn' : ''}`}>{status}{pending.status === 'scheduled' && left ? <> · <Kbd k="z" /> undo</> : null}</span>
      </header>
      <div className="body-text">{pending.text}</div>
    </article>
  )
}
